import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { RotateCw, Crop as CropIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal photo cropper for ID badge photos.
 * The aspect ratio is locked to the EXACT badge photo area (88×102 px → 22:25 ≈ 0.8627)
 * so the cropped output is NEVER squashed or further cropped when displayed on the badge.
 * Output: high-resolution JPEG (1320×1530, ~360dpi at print) via onSave(dataUrl).
 */
// Badge photo area = 88×102 px (see .badge-photo in index.css).
// Output canvas = 15× scale-up of the badge area for print quality at 300+ dpi.
const BADGE_PHOTO_W = 88;
const BADGE_PHOTO_H = 102;
const BADGE_PHOTO_ASPECT = BADGE_PHOTO_W / BADGE_PHOTO_H;
const OUTPUT_W = BADGE_PHOTO_W * 15;  // 1320
const OUTPUT_H = BADGE_PHOTO_H * 15;  // 1530

export const PhotoCropDialog = ({ open, src, onClose, onSave, title = "Ritaglia foto" }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [saving, setSaving] = useState(false);

    const onCropComplete = useCallback((_croppedArea, croppedAreaPixelsArg) => {
        setCroppedAreaPixels(croppedAreaPixelsArg);
    }, []);

    const handleSave = async () => {
        if (!croppedAreaPixels) return;
        setSaving(true);
        try {
            const dataUrl = await produceCroppedJpeg(src, croppedAreaPixels, rotation);
            onSave(dataUrl);
            // Reset state
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setRotation(0);
            setCroppedAreaPixels(null);
        } catch (e) {
            toast.error("Errore durante il ritaglio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl" data-testid="photo-crop-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <CropIcon className="h-5 w-5" /> {title}
                    </DialogTitle>
                    <DialogDescription>
                        Trascina per spostare, usa il cursore per ingrandire. L'area mostrata corrisponde <b>esattamente</b> a come la foto apparirà sul tesserino (proporzione 22:25 — la foto non verrà schiacciata né ulteriormente ritagliata).
                    </DialogDescription>
                </DialogHeader>

                {/* Cropper area */}
                <div className="relative h-[420px] w-full overflow-hidden rounded-md bg-slate-900" data-testid="cropper-area">
                    {src && (
                        <Cropper
                            image={src}
                            crop={crop}
                            zoom={zoom}
                            rotation={rotation}
                            aspect={BADGE_PHOTO_ASPECT}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                            objectFit="contain"
                            showGrid
                        />
                    )}
                </div>

                {/* Controls */}
                <div className="space-y-3">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">
                            Zoom: <span className="font-mono-numbers">{zoom.toFixed(2)}x</span>
                        </label>
                        <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.01}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="w-full accent-[#376DB3]"
                            data-testid="zoom-slider"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRotation((r) => (r + 90) % 360)}
                        className="gap-2"
                        data-testid="rotate-btn"
                    >
                        <RotateCw className="h-4 w-4" />
                        Ruota 90°
                    </Button>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving} data-testid="crop-cancel-btn">
                        Annulla
                    </Button>
                    <Button onClick={handleSave} disabled={saving || !croppedAreaPixels} className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="crop-save-btn">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Salva ritaglio
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Helpers --------------------------------------------------------------------

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

/**
 * Produce a cropped JPEG at the badge aspect ratio (1320x1530) from the source
 * image at the given pixel crop. Output proportions ALWAYS match the badge photo
 * area so no further cropping or stretching happens at render time.
 */
const produceCroppedJpeg = async (src, pixelCrop, rotation) => {
    const image = await loadImage(src);
    const radians = (rotation * Math.PI) / 180;

    // Rotated bounding box of the source image
    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));
    const rotW = image.width * cos + image.height * sin;
    const rotH = image.width * sin + image.height * cos;

    // 1. Render the rotated full image to a buffer canvas
    const buf = document.createElement("canvas");
    buf.width = rotW;
    buf.height = rotH;
    const bctx = buf.getContext("2d");
    bctx.imageSmoothingEnabled = true;
    bctx.imageSmoothingQuality = "high";
    bctx.translate(rotW / 2, rotH / 2);
    bctx.rotate(radians);
    bctx.drawImage(image, -image.width / 2, -image.height / 2);

    // 2. Cut the cropped region from the rotated buffer onto the final output canvas (badge ratio)
    const out = document.createElement("canvas");
    out.width = OUTPUT_W;
    out.height = OUTPUT_H;
    const octx = out.getContext("2d");
    octx.imageSmoothingEnabled = true;
    octx.imageSmoothingQuality = "high";
    octx.drawImage(
        buf,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        OUTPUT_W,
        OUTPUT_H,
    );
    return out.toDataURL("image/jpeg", 0.92);
};
